import type { JobStatus, Urgency } from '../shared/api';

export const money = (n: number | string | null | undefined) =>
  n === null || n === undefined || n === ''
    ? '—'
    : `$${Number(n).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

export const centsToDollars = (cents: number | null | undefined) => money((cents ?? 0) / 100);

export const fmtDate = (
  iso: string | null | undefined,
  opts: Intl.DateTimeFormatOptions = { month: 'short', day: 'numeric', year: 'numeric' },
) => {
  if (!iso) return '—';
  const d = iso.length === 10 ? new Date(`${iso}T12:00:00`) : new Date(iso);
  return Number.isNaN(d.getTime()) ? '—' : d.toLocaleDateString('en-US', opts);
};

export const fmtDateTime = (iso: string | null | undefined) =>
  iso ? fmtDate(iso, { month: 'short', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit' }) : '—';

export const todayISO = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
};

export const daysUntil = (iso: string | null | undefined): number | null => {
  if (!iso) return null;
  const target = new Date(`${iso.slice(0, 10)}T12:00:00`);
  if (Number.isNaN(target.getTime())) return null;
  return Math.round((target.getTime() - new Date(`${todayISO()}T12:00:00`).getTime()) / 86_400_000);
};

/**
 * Deadline urgency by days remaining: passed or today is urgent, three days is
 * soon, a week is worth watching. Used for every deadline the dashboard shows.
 */
/**
 * 'info' is not a deadline state — nothing computes it from a date. It marks a
 * step that is done and correct but still needs a human errand, like a
 * notarized NOC waiting to be recorded at the clerk.
 */
export type Tone = 'urgent' | 'soon' | 'watch' | 'ok' | 'info' | 'none';

export const toneForDays = (days: number | null): Tone => {
  if (days === null) return 'none';
  if (days <= 0) return 'urgent';
  if (days <= 3) return 'soon';
  if (days <= 7) return 'watch';
  return 'ok';
};

export const toneForDeadline = (iso: string | null | undefined): Tone => toneForDays(daysUntil(iso));

/** Maps the backend's own urgency verdict onto the same four tones. */
export const toneForUrgency = (u: Urgency | null | undefined): Tone => {
  switch (u) {
    case 'missed':
    case 'urgent':
      return 'urgent';
    case 'warning':
      return 'watch';
    case 'good':
      return 'ok';
    default:
      return 'none';
  }
};

export const TONE_TEXT: Record<Tone, string> = {
  urgent: 'text-urgent',
  soon: 'text-soon',
  watch: 'text-watch',
  ok: 'text-ok',
  info: 'text-tint',
  none: 'text-dim',
};

export const TONE_BADGE: Record<Tone, string> = {
  urgent: 'bg-urgent/15 text-urgent border-urgent/40',
  soon: 'bg-soon/15 text-soon border-soon/40',
  watch: 'bg-watch/15 text-watch border-watch/40',
  ok: 'bg-ok/15 text-ok border-ok/40',
  info: 'bg-tint/15 text-tint border-tint/40',
  none: 'bg-surface-alt text-dim border-border',
};

export const daysLabel = (days: number | null): string => {
  if (days === null) return 'Not set';
  if (days === 0) return 'Due today';
  if (days < 0) return `${Math.abs(days)}d overdue`;
  return `${days}d left`;
};

export const STATUS_LABELS: Record<JobStatus, string> = {
  new: 'New',
  property_verified: 'Property Verified',
  noo_prepared: 'NTO Prepared',
  noo_sent: 'NTO Sent',
  noo_delivered: 'NTO Delivered',
  active: 'Active',
  invoiced: 'Invoiced',
  partially_paid: 'Partially Paid',
  dispute: 'Dispute',
  litigation: 'Litigation',
  closed: 'Closed',
};

export const STATUS_ORDER = Object.keys(STATUS_LABELS) as JobStatus[];

/** Jobs that are still running — the "active" count on the overview. */
export const OPEN_STATUSES: JobStatus[] = STATUS_ORDER.filter((s) => s !== 'closed');

export const DOC_LABELS: Record<string, string> = {
  notice_to_owner: 'Notice to Owner',
  waiver: 'Lien Waiver',
  demand_letter: 'Demand Letter',
  claim_of_lien: 'Claim of Lien',
  certificate_of_satisfaction: 'Certificate of Satisfaction',
  noc_photo: 'NOC Photo',
  notice_of_commencement: 'Notice of Commencement',
  law_firm_referral: 'Law Firm Referral',
  affidavit: 'Final Payment Affidavit',
};

export const WAIVER_LABELS = {
  conditional_partial: 'Conditional — Progress Payment',
  unconditional_partial: 'Unconditional — Progress Payment',
  conditional_final: 'Conditional — Final Payment',
  unconditional_final: 'Unconditional — Final Payment',
} as const;

export const ROLE_LABELS = { prime: 'Prime / Owner-direct', gc: 'General Contractor', sub: 'Subcontractor' } as const;

/** The new-job question "who hired you?", worded so suppliers and fabricators find themselves too. */
export const ROLE_OPTION_LABELS = {
  prime: 'The property owner hired us directly (prime / owner-direct)',
  gc: 'We run the job, but our contract is not with the owner (GC)',
  sub: 'A contractor or sub hired us or bought from us (sub or supplier)',
} as const;

/** "What are you furnishing on this job?" */
export const WORK_TYPE_LABELS = {
  labor_only: 'Labor only',
  labor_materials: 'Labor and materials',
  materials_only: 'Materials only (we deliver, others install)',
} as const;

export const DELIVERED_TO_LABELS = {
  job_site: 'Delivered to the job site',
  contractor_yard: 'Delivered to a yard or shop',
  picked_up: 'Picked up (will-call)',
} as const;

export const PERMISSION_LABELS = {
  admin: 'Admin',
  manager: 'Manager',
  field: 'Field',
  read_only: 'Read only',
} as const;
